import { useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  PublicSiteBranding,
  PublicSiteBrandingInput,
  QuestionnairePublic,
} from '@updater/consumer-graph';
import {
  PublicSiteBrandingQuery,
  QuestionnaireByTokenQuery,
  SubmitPublicQuestionnaireAnswerMutation,
} from 'utils/queries';

interface QuestionnaireByTokenData {
  questionnaireByToken: Pick<
    QuestionnairePublic,
    'divisionUuid' | 'firstName' | 'status' | 'questionnaireKey'
  > & { unitNumber?: string | null };
}

interface PublicSiteBrandingData {
  publicSiteBranding: PublicSiteBranding;
}

interface SubmitAnswerData {
  submitPublicQuestionnaireAnswer: { success: boolean };
}

interface SubmitAnswerVariables {
  input: { token: string; response: string };
}

export const useQuestionnaire = (token?: string, option?: string) => {
  const {
    data: questionnaireData,
    loading: questionnaireLoading,
    error: questionnaireError,
  } = useQuery<QuestionnaireByTokenData>(QuestionnaireByTokenQuery, {
    variables: { token },
    skip: !token,
  });

  const divisionUuid = questionnaireData?.questionnaireByToken?.divisionUuid;

  const { data: brandingData, loading: brandingLoading } = useQuery<
    PublicSiteBrandingData,
    { input: PublicSiteBrandingInput }
  >(PublicSiteBrandingQuery, {
    variables: { input: { uuid: divisionUuid ?? '' } },
    skip: !divisionUuid,
  });

  const [
    submitAnswer,
    { data: mutationData, loading: mutationLoading, error: mutationError },
  ] = useMutation<SubmitAnswerData, SubmitAnswerVariables>(
    SubmitPublicQuestionnaireAnswerMutation
  );

  useEffect(() => {
    if (token && option) {
      submitAnswer({ variables: { input: { token, response: option } } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, option]);

  return {
    loading: questionnaireLoading || brandingLoading || mutationLoading,
    error: questionnaireError || mutationError,
    questionnaire: questionnaireData?.questionnaireByToken,
    siteBranding: brandingData?.publicSiteBranding.entityBrandings?.find(
      (entity) => entity && entity.kind === 'property'
    ),
    submitResult: mutationData?.submitPublicQuestionnaireAnswer,
  };
};
